/* eslint-disable no-unused-vars */
import { FC, useEffect, useMemo, useState } from 'react';
import { Transition } from '@headlessui/react';
import cx from 'classnames';
import CrossMarkAlt from 'components/Icons/CrossMarkAlt';
import PlusAlt from 'components/Icons/PlusAlt';
import Spinner from 'components/Spinner/Spinner';
import { PD_RANGE_BY_FRAME_SIZE, PD_RANGE_BY_FRAME_SIZE_MONO } from 'constants/pdRange';
import useUpdateEffect from 'hooks/useUpdateEffect';
import useWindowSize from 'hooks/useWindowSize';
import FullscreenOverlay from './FullscreenOverlay';
import OverlappingDropdown from './OverlappingDropdown';
import UIRadio from './UIRadio';
import { format } from 'date-fns';
import PdInputOverlappingDropdown from './PdInputOverlappingDropdown';
export interface BinocularPD {
  id?: Number | string;
  pd: Number | string;
  created_at?: string;
}
export interface MonocularPD {
  id?: Number | string;
  rightHeight: string | Number;
  right: string | Number;
  leftHeight: string | Number;
  left: string | Number;
  pd: string | Number;
  created_at?: string;
}

interface PDSelectDropdownProps {
  value?: BinocularPD | MonocularPD;
  options: Array<BinocularPD | MonocularPD>;
  // eslint-disable-next-line no-unused-vars
  onSelected?: (value: BinocularPD | MonocularPD) => void;
  // eslint-disable-next-line no-unused-vars
  onAdded?: AddPdHandler;
  placeholder?: string;
  frameSize?: string;
  pdInput?: boolean;
}

const isEmptyPDValue = (value: any) => {
  return !value || value === '0';
};

const PDSelectDropdown: FC<PDSelectDropdownProps> = ({
  value,
  options,
  onSelected,
  placeholder = 'Please Select',
  pdInput = false
}) => {
  const [isActive, setIsActive] = useState(false);
  const optionSelectedHandler = (option: BinocularPD | MonocularPD) => {
    onSelected?.(option);
    setIsActive(false);
  };
  const isMonocular = (option: MonocularPD) => option.left || option.right;
  if (!pdInput) {
    return (
      <OverlappingDropdown
        active={isActive}
        updateActiveState={setIsActive}
        placeholder={placeholder}
        value={
          value && (value.pd || (value as MonocularPD).left) ? (
            isMonocular(value as MonocularPD) ? (
              <PDSelectDropdownMonocularPDOption option={value as MonocularPD} />
            ) : (
              <PDSelectDropdownBinocularPDOption option={value as BinocularPD} />
            )
          ) : undefined
        }>
        {!options?.length ? (
          <PDSelectDropdownEmptyState />
        ) : (
          <div className="text-[14px]">
            <PDSelectDropdownOptionList
              options={options}
              onSelected={optionSelectedHandler}
            />
          </div>
        )}
      </OverlappingDropdown>
    );
  } else {
    return (
      <PdInputOverlappingDropdown
        value={value}
        placeholder={placeholder}
        active={isActive}
        updateActiveState={setIsActive}
        onChangePd={onSelected}
        pdValue={value}
        type={'prescription'}
      >
        {options.reverse().map((option, index) => {
          return (
            <div
              onClick={() => {
                onSelected?.(option.pd as any);
              }}
              className="group border-b border-brand-grey500 text-brand-black25 transition-colors first:mt-[-12px] last:border-none sm:hover:text-brand-orange"
              key={option.pd as any}>
              <div className="cursor-pointer py-[6px]" data-testid={`pd-option-${option.pd}`}>{option.pd}</div>
            </div>
          )
        })}
      </PdInputOverlappingDropdown>
    )
  }
};

const PDSelectDropdownOptionList: FC<{
  options: Array<BinocularPD | MonocularPD>;
  // eslint-disable-next-line no-unused-vars
  onSelected?: (option: BinocularPD | MonocularPD) => void;
}> = ({ options, onSelected }) => {
  const isMonocular = (option: MonocularPD) =>
    !isEmptyPDValue(option.left) && !isEmptyPDValue(option.right);


  var newOptions: Array<BinocularPD | MonocularPD> = [];
  options.filter((option: any) => option !== null).map((option: any) => {
    var check = newOptions.filter((newOption: any) => {
      if (option &&
        option.pd == null &&
        newOption.pd == null &&
        newOption.left == option.left &&
        newOption.leftHeight == option.leftHeight &&
        newOption.right == option.right &&
        newOption.rightHeight == option.rightHeight
      ) {
        return newOption;
      }

      if (option && option.pd == newOption.pd && option.pd !== null) {
        return newOption;
      }
    }).length;
    if (check == 0) {
      newOptions.push(option);
    }
  });

  var binoculars: any = [];
  var monoculars: any = [];
  var binocularWithHeight: any = [];

  newOptions.map((newOption: any) => {
    if (newOption.right && newOption.rightHeight == null) {
      binoculars.push(newOption);
    } else if (newOption.right && newOption.rightHeight != null) {
      binocularWithHeight.push(newOption);
    } else {
      monoculars.push(newOption);
    }
  })

  var newOptionsWith: Array<BinocularPD | MonocularPD> = [...monoculars, ...binocularWithHeight, ...binoculars].sort((a: any, b: any) => {
    if (a.created_at > b.created_at) return -1;
    if (a.created_at < b.created_at) return 1;
    return 0;
  });

  return (
    <div>
      {/* {JSON.stringify(newOptionsWith)} */}
      {newOptionsWith.map((option, index) => {
        return (
          <div
            key={index + '_' + (option.id || '-')}
            onClick={() => onSelected?.(option)}
            className="group border-b border-brand-grey500 text-brand-black25 transition-colors first:mt-[-12px] last:border-none sm:hover:text-brand-orange">
            {isMonocular(option as MonocularPD) ? (
              <PDSelectDropdownMonocularPDOption
                option={option as MonocularPD}
              />
            ) : (
              <PDSelectDropdownBinocularPDOption
                option={option as BinocularPD}
              />
            )}
          </div>
        );
      })}
    </div>
  );
};

export const PDSelectDropdownBinocularPDOption: FC<{ option: BinocularPD }> = ({
  option
}) => <div className="cursor-pointer py-[6px] ">{option.pd} {option?.created_at && <span className="text-brand-grey450"> - (Created on {format(new Date(option.created_at), 'dd.MM.yyy')} )</span>}</div>;

export const PDSelectDropdownMonocularPDOption: FC<{ option: MonocularPD }> = ({
  option
}) => (
  <div className="flex cursor-pointer flex-col py-[10px]">
    <div className="flex">
      {!!option.pd && <span className="font-medium">{option.pd}</span>}
      {(option?.created_at && (option.left && option.right && option.pd)) && <span className="text-brand-grey450 ml-2 whitespace-nowrap"> (Created on {format(new Date(option.created_at), 'dd.MM.yyy')} )</span>}
    </div>
    <div className='flex items-center'>
      <div className='flex-col min-w-[100px]'>
        {!!option.right && (
          <div>
            <span className="mr-[6px] text-brand-black100 transition-colors group-hover:text-brand-orange">
              R
            </span>
            {option.right}mm @dist
            {!isEmptyPDValue(option.rightHeight) && (
              <>, Height {option.rightHeight} @center</>
            )}
          </div>
        )}
        {!!option.left && (
          <span>
            <span className="mr-[6px] text-brand-black100 transition-colors group-hover:text-brand-orange">
              L
            </span>
            {option.left}mm @dist
            {!isEmptyPDValue(option.leftHeight) && (
              <>, Height {option.leftHeight} @center</>
            )}
          </span>
        )}
      </div>
      {(option?.created_at && !(option.left && option.right && option.pd)) && <span className="text-brand-grey450 ml-2 whitespace-nowrap"> (Created on {format(new Date(option.created_at), 'dd.MM.yyy')} )</span>}
    </div>

  </div>
);

export const PDSelectDropdownEmptyState = () => (
  <div>
    <p className="text-center text-[12px] font-medium text-brand-grey450">
      You do not have a specified PD value.
    </p>
  </div>
);

export const PDSelectDropdownWithAdd: FC<
  PDSelectDropdownProps & { addLoading?: boolean, onModeChanged?: (mode: 'select' | 'add') => void }
> = ({ value, options, onSelected, onAdded, addLoading = false, onModeChanged, frameSize }) => {
  const [isNewPDFormVisible, setIsNewPDFormVisible] = useState(false);

  const { width } = useWindowSize();

  useUpdateEffect(() => {
    if (!onModeChanged) return;

    onModeChanged(isNewPDFormVisible ? 'add' : 'select');
  }, [isNewPDFormVisible]);

  useEffect(() => {
    setIsNewPDFormVisible(false);
  }, [value]);

  const [pdRange, setPdRange] = useState(PD_RANGE_BY_FRAME_SIZE.M)

  useEffect(() => {
    if (frameSize) setPdRange(PD_RANGE_BY_FRAME_SIZE[frameSize]);
  }, [frameSize])

  return (
    <div className="flex flex-col">
      <div className="flex flex-col justify-between sm:flex-row sm:items-center">
        <span className="mb-[12px] flex flex-shrink-0 justify-between font-medium sm:mr-[10px] sm:mb-0">
          <span>PD</span>
          <span
            className="flex items-center sm:hidden"
            onClick={() => setIsNewPDFormVisible(!isNewPDFormVisible)}>
            <PlusAlt fill="currentColor" />
            <span className="ml-[8px] mb-[2px]">Add PD</span>
          </span>
        </span>
        <div className="flex-auto flex-shrink-0 sm:mr-[10px]">
          <PDSelectDropdown
            value={value}
            options={options}
            onSelected={onSelected}
          />
        </div>
        <span
          className="hidden min-w-[90px] flex-shrink-0 cursor-pointer items-center justify-center font-medium transition-colors hover:text-brand-orange sm:flex"
          onClick={() => setIsNewPDFormVisible(!isNewPDFormVisible)}>
          {isNewPDFormVisible ? (
            <CrossMarkAlt fill="currentColor" />
          ) : (
            <PlusAlt fill="currentColor" />
          )}
          <span className="ml-[8px] mb-[3px]">
            {isNewPDFormVisible ? 'Close' : 'Add PD'}
          </span>
        </span>
      </div>
      {width > 768 ? (
        <Transition
          show={isNewPDFormVisible}
          enter="transition ease-in-out duration-[550ms]"
          enterFrom="opacity-0 translate-x-[-25px]"
          enterTo="translate-y-0 opacity-1"
          leave="transition ease-in-out duration-[200ms] transform"
          leaveFrom="translate-y-0 opacity-1"
          leaveTo="translate-x-[-25px] opacity-0">
          <div className="relative mt-[12px]">
            <div className="absolute top-[-18px] right-[26px] inline-block w-[24px] overflow-hidden">
              <div className=" h-[18px] w-[18px] origin-bottom-left rotate-45 transform bg-white"></div>
            </div>
            <NewPDForm
              range={pdRange}
              onAdded={(binoType, payload) => onAdded?.(binoType, payload)}
              loading={addLoading}
            />
          </div>
        </Transition>
      ) : (
        <FullscreenOverlay
          isVisible={isNewPDFormVisible}
          onToggleVisibility={() => setIsNewPDFormVisible(false)}
          title="Add PD">
          <NewPDForm
            range={pdRange}
            onAdded={(binoType, payload) => onAdded?.(binoType, payload)}
            loading={addLoading}
          />
        </FullscreenOverlay>
      )}
    </div>
  );
};

export const createPDRange = (
  maxRange = 72,
  start = 26,
  gap = 0.5
): Number[] => {
  let value = start - gap;
  const range: number[] = [];
  while (value < maxRange) {
    range.push(value + gap);
    value += gap;
  }
  return range;
};

type addPdPayload =
  | { pd: String | Number }
  | { left: { pd: String | Number }; right: { pd: String | Number } };

export type AddPdHandler = (
  pdType: 'bino' | 'mono',
  pdPayload: addPdPayload
) => void;

export const NewPDForm: FC<{
  range: { min: number, max: number },
  onAdded?: AddPdHandler;
  loading: boolean;
}> = ({ onAdded, loading, range }) => {
  const [pdType, setPdType] = useState('bino');

  return (
    <div className="sm:rounded-[8px] sm:bg-white sm:p-[18px]">
      <UIRadio
        options={[
          { text: 'I have one PD for both eyes, together', value: 'bino' },
          { text: 'I have one PD for each eye, separately', value: 'mono' }
        ]}
        value={pdType}
        onChange={setPdType}
      />
      <div className="mt-[12px] flex">
        {pdType === 'bino' ? (
          <div className="flex flex-col">
            <NewPDBinocularForm
              onAdded={(payload) => onAdded?.('bino', payload)}
              loading={loading}
              range={range}
            />
            <p className="text-[12px] text-brand-black25 mt-[8px]">Ranges between 33 - 80</p>
          </div>
        ) : (
          <div className="flex flex-col">
            <NewPDMonocularForm
              onAdded={(payload) => onAdded?.('mono', payload)}
              loading={loading}
              range={range}
            />
            <p className="text-[12px] text-brand-black25 mt-[8px]">Ranges between 16.5 - 40</p>
          </div>
        )}
      </div>
    </div>
  );
};

export const NewPDBinocularForm: FC<{
  range: { min: number, max: number },
  // eslint-disable-next-line no-unused-vars
  onAdded: (payload: { pd: Number | String }) => void;
  loading: boolean;
}> = ({ onAdded, loading, range }) => {
  const [pdValue, setPdValue] = useState<any>();
  const pdRange = createPDRange(range.max, range.min)
    .map((pdValue) => ({
      id: 0,
      pd: pdValue
    }))
    .reverse();

  const updatePdValue = (pdOption: any) => {
    setPdValue((pdOption));
  };

  const selectedPd = useMemo(() => {
    if (!pdValue) return;
    return pdValue;
  }, [pdValue]);

  return (
    <div className="flex w-full flex-col sm:flex-row">
      <div className="mb-[12px] max-w-none flex-auto sm:mr-[10px] sm:mb-0 sm:max-w-[233px]">
        <PDSelectDropdown
          options={pdRange}
          value={selectedPd as unknown as MonocularPD}
          onSelected={updatePdValue}
          placeholder="Select"
          pdInput={true}
        />
      </div>
      <NewPDFormSubmitButton
        loading={loading}
        disabled={!pdValue}
        onClick={() => selectedPd && onAdded({ pd: selectedPd })}>
        Add
      </NewPDFormSubmitButton>
    </div>
  );
};

export const NewPDMonocularForm: FC<{
  range: { min: number, max: number },
  // eslint-disable-next-line no-unused-vars
  onAdded: (payload: {
    left: { pd: Number | String };
    right: { pd: Number | String };
  }) => void;
  loading: boolean;
}> = ({ onAdded, loading, range }) => {
  const [pdValue, setPdValue] = useState({ left: '', right: '' });

  const pdRange = createPDRange(PD_RANGE_BY_FRAME_SIZE_MONO['M'].max, PD_RANGE_BY_FRAME_SIZE_MONO['M'].min)
    .map((pdValue) => ({
      id: 0,
      pd: pdValue
    }))
    .reverse();

  const updatePdValue = (direction: 'left' | 'right', pd: number) => {

    setPdValue({
      ...pdValue,
      [direction]: pd
    });
  };

  const selectedPd = useMemo(
    () => ({
      left: {
        id: 0,
        pd: pdValue.left
      },
      right: {
        id: 0,
        pd: pdValue.right
      }
    }),
    [pdValue]
  );

  return (
    <div className="flex w-full flex-col sm:flex-row">
      <div className="mr-0 mb-[12px] flex flex-shrink-0 sm:mb-0 sm:mr-[10px] sm:flex-row">
        <div className="mr-[10px] flex-1 w-[130px]">
          <PDSelectDropdown
            placeholder="Right"
            options={pdRange}
            value={selectedPd.right.pd as any}
            onSelected={(pd: any) => {
              //console.log('bb', pd)
              updatePdValue('right', pd);
            }}
            pdInput={true}
          />
        </div>
        <div className="flex-1 -w-[130px]">
          <PDSelectDropdown
            placeholder="Left"
            options={pdRange}
            value={selectedPd.left.pd as any}
            onSelected={(pd: any) => updatePdValue('left', pd)}
            pdInput={true}
          />
        </div>
      </div>
      <NewPDFormSubmitButton
        disabled={!pdValue.left || !pdValue.right}
        loading={loading}
        onClick={() =>
          selectedPd.left.pd &&
          selectedPd.right.pd &&
          onAdded({
            left: { pd: selectedPd.left.pd },
            right: { pd: selectedPd.right.pd }
          })
        }>
        Add
      </NewPDFormSubmitButton>
    </div>
  );
};

export const NewPDFormSubmitButton: FC<{
  disabled?: boolean;
  onClick: () => void;
  loading?: boolean;
}> = ({ children, disabled, onClick, loading }) => {
  return (
    <button
      onClick={() => {
        if (disabled || loading) return;
        onClick();
      }}
      className={cx(
        'flex h-[40px] w-full items-center justify-center rounded-[6px] px-[30px] text-center font-medium text-white transition-colors duration-200 sm:w-auto',
        {
          'cursor-pointer bg-brand-black100 hover:text-brand-orange':
            !disabled && !loading,
          'cursor-not-allowed bg-brand-grey350': disabled || loading,
          'justify-center': loading
        }
      )}>
      {loading ? <Spinner fill="#fff" /> : children}
    </button>
  );
};

export default PDSelectDropdown;
